import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle, ShieldCheck, Envelope, FileText, Clock, WarningCircle,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Defensive JSON reader (evita "body stream already read" causato da extension/SW)
async function safeJson(res) {
  try {
    const text = await res.text();
    return text ? JSON.parse(text) : {};
  } catch (e) {
    return {};
  }
}

// Selettore prefisso internazionale con bandiera. Default IT.
const COUNTRY_CODES = [
  { code: '+39', flag: '🇮🇹', name: 'Italia' },
  { code: '+33', flag: '🇫🇷', name: 'Francia' },
  { code: '+49', flag: '🇩🇪', name: 'Germania' },
  { code: '+34', flag: '🇪🇸', name: 'Spagna' },
  { code: '+44', flag: '🇬🇧', name: 'Regno Unito' },
  { code: '+41', flag: '🇨🇭', name: 'Svizzera' },
  { code: '+43', flag: '🇦🇹', name: 'Austria' },
  { code: '+1', flag: '🇺🇸', name: 'USA/Canada' },
];

// Sezioni del form di modifica cliente pre-firma (Sprint 49).
// I campi rispecchiano CUSTOMER_QUICK_EDIT_FIELDS lato backend.
const EDIT_SECTIONS = [
  { title: 'Contatti', fields: [
    { k: 'email', label: 'Email', type: 'email' },
    { k: 'telefono', label: 'Telefono', type: 'tel' },
    { k: 'codice_destinatario_pec', label: 'PEC / Codice SDI' },
  ]},
  { title: 'Rappresentante legale', fields: [
    { k: 'legale_rappresentante', label: 'Nome e cognome', full: true },
  ]},
  { title: 'Sede legale', fields: [
    { k: 'sede_legale_indirizzo', label: 'Indirizzo', full: true },
    { k: 'sede_legale_comune', label: 'Comune' },
    { k: 'sede_legale_prov_cap', label: 'Provincia / CAP' },
  ]},
  { title: 'Indirizzo fornitura', fields: [
    { k: 'indirizzo_fornitura', label: 'Indirizzo', full: true },
    { k: 'comune_fornitura', label: 'Comune' },
    { k: 'prov_cap_fornitura', label: 'Provincia / CAP' },
  ]},
  { title: 'Indirizzo recapito (fatture e comunicazioni)', fields: [
    { k: 'indirizzo_recapito', label: 'Indirizzo', full: true },
    { k: 'comune_recapito', label: 'Comune' },
    { k: 'provincia_recapito', label: 'Provincia' },
    { k: 'cap_recapito', label: 'CAP' },
  ]},
  { title: 'Pagamento', fields: [
    { k: 'iban', label: 'IBAN', full: true, mono: true },
    { k: 'metodo_pagamento', label: 'Metodo di pagamento' },
  ]},
];

function PhoneInputWithFlag({ value, onChange, prefix, setPrefix, disabled }) {
  // value contiene SOLO la parte locale (senza prefix). Il caller compone full = prefix + value
  return (
    <div className="flex gap-2">
      <select
        value={prefix}
        onChange={(e) => setPrefix(e.target.value)}
        disabled={disabled}
        className="text-base font-mono py-3 px-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-orange-500 bg-white"
        data-testid="phone-prefix-select"
        style={{ minWidth: '110px' }}
      >
        {COUNTRY_CODES.map(c => (
          <option key={c.code} value={c.code}>
            {c.flag} {c.code}
          </option>
        ))}
      </select>
      <input
        type="tel"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
        placeholder="333 1234567"
        disabled={disabled}
        className="flex-1 text-base font-mono py-3 px-4 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-orange-500"
        data-testid="sms-phone-input"
        inputMode="numeric"
        autoComplete="tel-national"
      />
    </div>
  );
}

export default function SignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('review');  // review | otp | done | error
  const [error, setError] = useState('');
  const [otp, setOtp] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [otpChannel, setOtpChannel] = useState(null); // 'email' | 'sms'
  const [phoneInput, setPhoneInput] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('+39');
  const [phoneStep, setPhoneStep] = useState(false); // showing phone confirm box
  // Sprint 43 - Customer self-edit pre-firma
  const [editableFields, setEditableFields] = useState(null); // null while loading, false if not editable
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  const fetchEditable = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/sign/${token}/editable-fields`);
      const data = await safeJson(res);
      if (res.ok && data.editable) {
        setEditableFields(data.fields);
        const form = {};
        Object.entries(data.fields).forEach(([k, v]) => {
          form[k] = k.startsWith('consenso_')
            ? (v || '').toString().toUpperCase() === 'SI'
            : (v || '');
        });
        setEditForm(form);
      } else {
        setEditableFields(false);
      }
    } catch (e) { setEditableFields(false); }
  }, [token]);

  const submitCustomerEdit = async () => {
    setEditSubmitting(true);
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => {
        payload[k] = k.startsWith('consenso_') ? (v ? 'SI' : 'NO') : v;
      });
      const res = await fetch(`${API}/api/sign/${token}/quick-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await safeJson(res);
      if (res.ok) {
        toast.success('Modifiche salvate. Il documento è stato aggiornato — ora puoi procedere con la firma.', { duration: 6000 });
        setEditOpen(false);
        await fetchEditable();
      } else {
        toast.error(data.detail || 'Errore aggiornamento');
      }
    } catch (e) {
      toast.error('Errore di rete');
    } finally {
      setEditSubmitting(false);
    }
  };

  const fetchInfo = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/sign/${token}/info`);
      const data = await safeJson(res);
      if (res.ok) {
        setInfo(data);
        if (data.status === 'signed') {
          setStep('done');
          setSignedAt(data.signed_at);
        }
      } else {
        setError(data.detail || 'Link non valido o scaduto');
        setStep('error');
      }
    } catch (e) {
      setError('Errore di connessione');
      setStep('error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchInfo(); fetchEditable(); }, [fetchInfo, fetchEditable]);

  // Countdown
  useEffect(() => {
    if (!otpExpiresAt) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(otpExpiresAt) - new Date()) / 1000));
      setRemaining(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [otpExpiresAt]);

  const requestOtp = async () => {
    setRequesting(true);
    try {
      const res = await fetch(`${API}/api/sign/${token}/request-otp`, { method: 'POST' });
      const data = await safeJson(res);
      if (res.ok) {
        setStep('otp');
        setOtpChannel('email');
        setOtpExpiresAt(data.expires_at);
        toast.success('Codice OTP inviato alla tua email');
      } else {
        const msg = data.detail || 'Errore invio OTP';
        toast.error(msg, { duration: 8000,
          action: info?.sms_available ? { label: 'Prova SMS', onClick: () => setPhoneStep(true) } : undefined });
      }
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setRequesting(false);
    }
  };

  const requestOtpSms = async (phoneOverride = null) => {
    // Compone numero E.164: prefix + parte locale (senza spazi)
    const localPart = (phoneOverride || phoneInput || '').replace(/\D/g, '');
    if (!localPart || localPart.length < 6) {
      return toast.error('Numero non valido. Inserisci almeno 6 cifre.');
    }
    const phone = `${phonePrefix}${localPart}`;
    setRequesting(true);
    try {
      const res = await fetch(`${API}/api/sign/${token}/request-otp-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await safeJson(res);
      if (res.ok) {
        setStep('otp');
        setOtpChannel('sms');
        setOtpExpiresAt(data.expires_at);
        setPhoneStep(false);
        toast.success(`Codice OTP inviato via SMS a ${data.phone_masked}`);
      } else {
        const det = data.detail;
        const msg = det?.message || (typeof det === 'string' ? det : data.error || 'Errore invio SMS');
        toast.error(msg, { duration: 9000,
          action: { label: 'Prova email', onClick: () => { setPhoneStep(false); requestOtp(); } } });
      }
    } catch (e) {
      // Mostra il vero errore se disponibile (network, CORS, timeout...)
      toast.error(`Errore: ${e?.message || 'connessione interrotta'}. Riprova.`, { duration: 6000 });
    } finally {
      setRequesting(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return toast.error('Inserisci il codice di 6 cifre');
    setVerifying(true);
    try {
      const res = await fetch(`${API}/api/sign/${token}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp }),
      });
      const data = await safeJson(res);
      if (res.ok) {
        setStep('done');
        setSignedAt(data.signed_at);
        toast.success('Contratto firmato!');
      } else {
        toast.error(data.detail || 'Errore firma');
      }
    } catch (e) {
      toast.error(`Errore: ${e?.message || 'connessione interrotta'}. Riprova.`);
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Caricamento...</p>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-red-100 p-8 text-center">
          <WarningCircle size={64} className="mx-auto text-red-500 mb-4" weight="duotone" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Link non valido</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-page">
      {/* HEADER */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
              <ShieldCheck size={24} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">{info?.company_name || 'Orange Energy'}</h1>
              <p className="text-xs text-slate-500">Firma elettronica del contratto</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck size={14} className="text-green-600" />
            Pagina sicura
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        {step === 'review' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Gentile/Spett.le {info?.ragione_sociale}
              </h2>
              <p className="text-slate-600 mb-6">
                Abbiamo predisposto la documentazione contrattuale di <strong>{info?.company_name || 'Orange Energy'}</strong>.
                Per finalizzare la sottoscrizione segui questi semplici passaggi:
              </p>

              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 rounded-lg bg-orange-50 border border-orange-100">
                  <div className="w-12 h-12 mx-auto rounded-full bg-orange-500 text-white font-bold flex items-center justify-center mb-2">1</div>
                  <p className="font-semibold text-slate-800">Visualizza i documenti</p>
                  <p className="text-xs text-slate-500 mt-1">Anteprima contratto</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-orange-50 border border-orange-100">
                  <div className="w-12 h-12 mx-auto rounded-full bg-orange-500 text-white font-bold flex items-center justify-center mb-2">2</div>
                  <p className="font-semibold text-slate-800">Ricevi codice OTP</p>
                  <p className="text-xs text-slate-500 mt-1">Via email a {info?.signer_email}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-orange-50 border border-orange-100">
                  <div className="w-12 h-12 mx-auto rounded-full bg-orange-500 text-white font-bold flex items-center justify-center mb-2">3</div>
                  <p className="font-semibold text-slate-800">Firma digitale</p>
                  <p className="text-xs text-slate-500 mt-1">Inserisci OTP e conferma</p>
                </div>
              </div>

              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <FileText size={20} className="text-orange-500" />
                Documenti da firmare ({info?.documents?.length || 0})
              </h3>
              <ul className="space-y-2 mb-6">
                {info?.documents?.filter(d => !d.startsWith("Documento d'identità")).map((d, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg">
                    <FileText size={16} className="text-slate-400" />
                    {d}
                  </li>
                ))}
                {info?.extra_attachments?.map(a => (
                  <li key={a.idx} className="flex items-center justify-between gap-2 text-sm text-indigo-900 bg-indigo-50 border border-indigo-100 px-3 py-2 rounded-lg"
                      data-testid={`extra-attachment-row-${a.idx}`}>
                    <span className="flex items-center gap-2">
                      <FileText size={16} className="text-indigo-500" />
                      {a.filename}
                      <span className="text-[10px] text-indigo-400 font-medium uppercase">allegato</span>
                    </span>
                    <a href={`${API}/api/sign/${token}/attachment/${a.idx}`} target="_blank" rel="noopener noreferrer"
                       className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 underline whitespace-nowrap"
                       data-testid={`extra-attachment-view-${a.idx}`}>
                      Visualizza
                    </a>
                  </li>
                ))}
                {info?.identity_doc?.available && (
                  <li className="flex items-center justify-between gap-2 text-sm text-blue-900 bg-blue-50 border border-blue-100 px-3 py-2 rounded-lg"
                      data-testid="identity-doc-row">
                    <span className="flex items-center gap-2">
                      <ShieldCheck size={16} className="text-blue-500" weight="fill" />
                      Documento d'identità {info.identity_doc.doc_type ? `(${info.identity_doc.doc_type})` : ''}
                      {info.identity_doc.doc_number ? ` · n. ${info.identity_doc.doc_number}` : ''}
                    </span>
                    <a href={`${API}/api/sign/${token}/identity-doc`} target="_blank" rel="noopener noreferrer"
                       className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                       data-testid="identity-doc-view-link">
                      Visualizza
                    </a>
                  </li>
                )}
              </ul>

              <a href={`${API}/api/sign/${token}/preview`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 font-medium mb-6"
                 data-testid="preview-contract-link">
                <FileText size={16} /> Apri anteprima contratto (PDF)
              </a>

              {/* Sprint 43 - Customer self-edit pre-firma */}
              {editableFields && !editOpen && (
                <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid="customer-edit-cta">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 text-sm mb-1">
                        Vuoi modificare qualcosa prima di firmare?
                      </h4>
                      <p className="text-xs text-slate-600">
                        Puoi correggere i tuoi dati anagrafici (indirizzi, IBAN, PEC/SDI, rappresentante legale),
                        i contatti e i consensi. Il documento verrà rigenerato e potrai firmare la versione aggiornata.
                      </p>
                    </div>
                    <button onClick={() => setEditOpen(true)}
                            className="px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-100 whitespace-nowrap"
                            data-testid="open-customer-edit-btn">
                      ✏️ Modifica
                    </button>
                  </div>
                </div>
              )}

              {editableFields && editOpen && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="customer-edit-form">
                  <h4 className="font-semibold text-amber-900 text-sm mb-3">Correggi i tuoi dati</h4>
                  <div className="space-y-4">
                    {EDIT_SECTIONS.map(section => (
                      <div key={section.title}>
                        <div className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-2">
                          {section.title}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {section.fields.map(f => (
                            <div key={f.k} className={f.full ? 'sm:col-span-2' : ''}>
                              <label className="text-xs text-slate-700 mb-1 block">{f.label}</label>
                              <input type={f.type || 'text'} value={editForm[f.k] ?? ''}
                                     onChange={e => setEditForm(prev => ({ ...prev, [f.k]: e.target.value }))}
                                     className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${f.mono ? 'font-mono' : ''}`}
                                     data-testid={`customer-edit-${f.k.replace(/_/g, '-')}`} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-amber-300">
                      <div className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-2">I miei consensi</div>
                      <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <input type="checkbox" className="mt-1" checked={!!editForm.consenso_marketing}
                               onChange={e => setEditForm(f => ({ ...f, consenso_marketing: e.target.checked }))}
                               data-testid="customer-edit-marketing" />
                        <span><strong>Marketing</strong> — accetto di ricevere comunicazioni commerciali</span>
                      </label>
                      <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
                        <input type="checkbox" className="mt-1" checked={!!editForm.consenso_terzi}
                               onChange={e => setEditForm(f => ({ ...f, consenso_terzi: e.target.checked }))}
                               data-testid="customer-edit-terzi" />
                        <span><strong>Comunicazione a terzi</strong> — autorizzo la condivisione dei miei dati con partner commerciali</span>
                      </label>
                      <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
                        <input type="checkbox" className="mt-1" checked={!!editForm.consenso_invio_documenti}
                               onChange={e => setEditForm(f => ({ ...f, consenso_invio_documenti: e.target.checked }))}
                               data-testid="customer-edit-invio-documenti" />
                        <span><strong>Invio documenti</strong> via email/PEC</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setEditOpen(false)} disabled={editSubmitting}
                            className="px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-50">
                      Annulla
                    </button>
                    <button onClick={submitCustomerEdit} disabled={editSubmitting}
                            className="flex-1 px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-50"
                            data-testid="customer-edit-submit-btn">
                      {editSubmitting ? 'Aggiornamento…' : 'Salva e aggiorna il documento'}
                    </button>
                  </div>
                  <p className="text-[11px] text-amber-700 mt-3">
                    ℹ️ Salvando le modifiche, il documento PDF verrà rigenerato. Se avevi già richiesto un codice OTP, dovrai richiederlo nuovamente.
                  </p>
                </div>
              )}

              <div className="border-t border-slate-200 pt-6">
                {!phoneStep ? (
                  <>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button onClick={requestOtp} disabled={requesting}
                              data-testid="request-otp-email-btn"
                              className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-lg transition-colors">
                        {requesting && otpChannel !== 'sms' ? 'Invio...' : '📧 Codice OTP via Email'}
                      </button>
                      {info?.sms_available && (
                        <button onClick={() => { setPhoneStep(true); setPhoneInput(''); }}
                                disabled={requesting}
                                data-testid="request-otp-sms-btn"
                                className="flex-1 bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-lg transition-colors">
                          📱 Codice OTP via SMS
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-3">
                      Email a <strong>{info?.signer_email}</strong>
                      {info?.sms_available && info?.signer_phone_hint &&
                        <> · SMS al numero che termina con <strong>***{info.signer_phone_hint}</strong></>}
                    </p>
                  </>
                ) : (
                  <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Conferma il numero di cellulare</p>
                    <p className="text-xs text-slate-500 mb-3">
                      Per sicurezza, conferma il numero del firmatario.
                      {info?.signer_phone_masked && <><br/>Numero in archivio: <strong className="font-mono">{info.signer_phone_masked}</strong></>}
                    </p>
                    <PhoneInputWithFlag
                      value={phoneInput}
                      onChange={setPhoneInput}
                      prefix={phonePrefix}
                      setPrefix={setPhonePrefix}
                      disabled={requesting}
                    />
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setPhoneStep(false)} disabled={requesting}
                              className="px-4 py-2 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-100"
                              data-testid="sms-phone-cancel">Annulla</button>
                      <button onClick={() => requestOtpSms()} disabled={requesting || !phoneInput}
                              className="flex-1 bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white font-semibold py-2 px-6 rounded-lg"
                              data-testid="sms-phone-confirm-send">
                        {requesting ? 'Invio SMS...' : '📱 Invia codice via SMS'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
              <strong>Valore legale:</strong> la sottoscrizione tramite OTP costituisce firma elettronica
              semplice (FES) ai sensi del Reg. UE 910/2014 (eIDAS) e dell'art. 21 del CAD.
            </div>
          </div>
        )}

        {step === 'otp' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md mx-auto text-center">
            <Envelope size={64} className="mx-auto text-orange-500 mb-4" weight="duotone" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Inserisci il codice OTP</h2>
            <p className="text-slate-600 mb-6 text-sm">
              {otpChannel === 'sms' ? (
                <>Abbiamo inviato un codice di 6 cifre via SMS a<br />
                <strong className="font-mono">{info?.signer_phone_masked}</strong></>
              ) : (
                <>Abbiamo inviato un codice di 6 cifre a<br />
                <strong>{info?.signer_email}</strong></>
              )}
            </p>

            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              data-testid="otp-input"
              className="w-full text-center text-3xl font-mono tracking-[0.5em] py-4 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-orange-500 mb-4"
            />

            {remaining > 0 ? (
              <p className="text-xs text-slate-500 mb-4 flex items-center justify-center gap-1">
                <Clock size={14} /> Codice valido per {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}
              </p>
            ) : (
              <p className="text-xs text-red-600 mb-4">Codice scaduto, richiedine uno nuovo</p>
            )}

            <button onClick={verifyOtp} disabled={verifying || otp.length !== 6}
                    data-testid="verify-otp-btn"
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold py-3 rounded-lg mb-3">
              {verifying ? 'Firma in corso...' : 'Firma il contratto'}
            </button>
            <button onClick={requestOtp} disabled={requesting}
                    className="text-sm text-slate-500 hover:text-orange-600">
              {requesting ? 'Invio...' : 'Non hai ricevuto il codice? Invia di nuovo'}
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="bg-white rounded-2xl shadow-sm border border-green-200 p-8 max-w-md mx-auto text-center">
            <CheckCircle size={80} className="mx-auto text-green-500 mb-4" weight="duotone" />
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Contratto firmato!</h2>
            <p className="text-slate-600 mb-6">
              {signedAt && (
                <>Firmato il <strong>{new Date(signedAt).toLocaleString('it-IT')}</strong></>
              )}
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-900 mb-6 text-left">
              <p className="mb-2">
                <strong>Hai ricevuto via email:</strong>
              </p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Copia del contratto firmato (PDF)</li>
                <li>Tutti i documenti contrattuali</li>
                <li>Certificato di firma elettronica con valore legale</li>
              </ul>
            </div>

            <a href={`${API}/api/sign/${token}/download`}
               className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg"
               data-testid="download-signed-btn">
              Scarica il contratto firmato
            </a>

            <p className="text-xs text-slate-500 mt-6">
              Conserva l'email ricevuta come ricevuta della sottoscrizione.<br />
              Per qualsiasi necessit&agrave; il tuo consulente rimane a tua disposizione.
            </p>
          </div>
        )}
      </main>

      <footer className="text-center text-xs text-slate-400 py-6">
        Powered by {info?.company_name || 'Orange Energy'} - Firma sicura conforme eIDAS
      </footer>
    </div>
  );
}
